<script setup lang="ts">
import { useTemplateRef, watch } from 'vue';
import { useEventListener } from '@vueuse/core';

import { ruText as t } from '@/constants/ru-text';

const isOpen = defineModel<boolean>();

watch(isOpen, () => (isOpen.value ? open() : close()));

const filterDialogEl = useTemplateRef<HTMLDialogElement>('find-replay-el');

const open = () => {
  isOpen.value = true;
  filterDialogEl.value?.showModal();
};

const close = () => {
  isOpen.value = false;
  filterDialogEl.value?.close();
};

const onCloseHandler = () => close();

useEventListener(filterDialogEl, 'close', onCloseHandler);
</script>

<template>
  <dialog
    ref="find-replay-el"
    class="dialog"
  >
    <div class="wrap">
      <div class="head">
        <button
          class="g-icon material-symbols-rounded close-btn round"
          :title="t.get('component-dialog-close-title')"
          @click="close()"
        >
          close
        </button>
        <h1 v-if="$slots.title">
          <slot name="title"></slot>
        </h1>
      </div>
      <slot @close="close()" />
    </div>
  </dialog>
</template>

<style scoped lang="postcss">
.dialog {
  --duration: 200ms;
  --function: ease-out;

  max-width: 100vw;
  max-height: 100vw;

  width: 100%;
  height: 100%;
  background: transparent;
  overflow: hidden;

  padding: var(--space-m-var_03);

  border: none;

  @media (--fhd-desktop) {
    padding: 36px 88px;
  }

  .wrap {
    display: grid;
    grid-template-rows: auto 1fr;

    margin: 0 auto;
    width: 100%;

    max-width: var(--container-max-width);

    .close-btn {
      background: transparent;
      padding: var(--space-s-var_02);
      color: var(--color-white-50);

      position: absolute;
      top: 0;
      right: 0;
      transform: translate(-52px, 30px);
      cursor: pointer;
      font-size: 28px;

      &:hover {
        background-color: var(--color-white-4);
        color: var(--color-white-80);
      }
    }

    h1 {
      padding-bottom: var(--space-m-var_03);
      margin-bottom: var(--space-m-var_03);
      border-bottom: 1px solid var(--color-white-12);
    }
  }
}

.dialog {
  opacity: 0;
  transition:
    opacity var(--duration) var(--function),
    overlay var(--duration) var(--function) allow-discrete,
    display var(--duration) var(--function) allow-discrete;
}

dialog[open] {
  opacity: 1;
}

@starting-style {
  dialog[open] {
    opacity: 0;
  }
}

dialog::backdrop {
  backdrop-filter: blur(0px);
  background-color: transparent;
  transition:
    background-color var(--duration) var(--function),
    backdrop-filter var(--duration) var(--function),
    overlay var(--duration) var(--function) allow-discrete,
    display var(--duration) var(--function) allow-discrete;
}

dialog[open]::backdrop {
  backdrop-filter: blur(80px);
  background-color: var(--color-deep-blue-85);
}

@starting-style {
  dialog[open]::backdrop {
    backdrop-filter: blur(0px);
    background-color: transparent;
  }
}
</style>
